import { db } from '@/db/db';
import { EXAM_PRESETS, type ExamPreset } from '@/constants/app';
import { shuffle } from './select';
import { recordAnswer } from './practice';
import type { ExamAnswer, ExamRun, Item, Session } from './types';

/**
 * Timed exams. The countdown is wall-clock based: only `endsAt` is stored, so
 * closing the app and reopening it does not buy any extra time.
 *
 * Exam runs are device-local on purpose. Syncing a half-finished exam between
 * two devices would be a way to lose answers, not gain anything.
 */

export function presetsFor(custom: ExamPreset[] | undefined): ExamPreset[] {
  return custom && custom.length > 0 ? custom : EXAM_PRESETS;
}

export interface StartExamInput {
  preset: ExamPreset;
  items: Item[];
  sessions: Session[];
  /** Empty means draw from everything. */
  sessionIds: string[];
  now?: number;
  rng?: () => number;
}

export async function startExam({
  preset,
  items,
  sessionIds,
  now = Date.now(),
  rng,
}: StartExamInput): Promise<ExamRun> {
  const pool = items.filter(
    (i) => !i.deleted && i.type !== 'flashcard' && (sessionIds.length === 0 || sessionIds.includes(i.sessionId)),
  );
  const picked = shuffle(pool, rng).slice(0, preset.questions);

  const run: ExamRun = {
    id: crypto.randomUUID(),
    presetId: preset.id,
    startedAt: now,
    endsAt: now + preset.minutes * 60 * 1000,
    itemIds: picked.map((i) => i.id),
    answers: {},
    submittedAt: null,
    sessionIds,
  };
  await db.exams.put(run);
  return run;
}

/** The exam still in flight on this device, if any. */
export async function activeExam(): Promise<ExamRun | undefined> {
  const runs = await db.exams.orderBy('startedAt').reverse().toArray();
  return runs.find((r) => r.submittedAt === null);
}

export async function saveAnswer(runId: string, answer: ExamAnswer): Promise<ExamRun | undefined> {
  const run = await db.exams.get(runId);
  if (!run || run.submittedAt !== null) return run;
  const next: ExamRun = { ...run, answers: { ...run.answers, [answer.itemId]: answer } };
  await db.exams.put(next);
  return next;
}

export function msRemaining(run: ExamRun, now = Date.now()): number {
  return Math.max(0, run.endsAt - now);
}

export function isExpired(run: ExamRun, now = Date.now()): boolean {
  return msRemaining(run, now) === 0;
}

export function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export interface ExamResult {
  run: ExamRun;
  total: number;
  correct: number;
  /** Multiple-choice graded on submit; short answers stay ungraded until self-graded. */
  perCategory: Array<{ category: string; correct: number; total: number }>;
  missed: Item[];
  needsSelfGrade: Item[];
}

/**
 * Marks the exam finished and grades what can be graded automatically. Short
 * answers are self-graded on the results screen afterwards.
 */
export async function submitExam(
  run: ExamRun,
  items: Item[],
  now = Date.now(),
): Promise<ExamResult> {
  const byId = new Map(items.map((i) => [i.id, i]));
  const questions = run.itemIds.map((id) => byId.get(id)).filter((i): i is Item => Boolean(i));

  const submitted: ExamRun = { ...run, submittedAt: now };
  await db.exams.put(submitted);

  const perCategory = new Map<string, { correct: number; total: number }>();
  const missed: Item[] = [];
  const needsSelfGrade: Item[] = [];
  let correct = 0;

  for (const item of questions) {
    const answer = run.answers[item.id];
    const entry = perCategory.get(item.category) ?? { correct: 0, total: 0 };
    entry.total += 1;

    if (item.type === 'mc') {
      const right = answer?.chosenIndex === item.answerIndex;
      if (right) {
        correct += 1;
        entry.correct += 1;
      } else {
        missed.push(item);
      }
      await recordAnswer({ item, correct: right, mode: 'exam' });
    } else {
      // Unanswered short questions still count as attempts, graded by hand.
      needsSelfGrade.push(item);
    }
    perCategory.set(item.category, entry);
  }

  return {
    run: submitted,
    total: questions.length,
    correct,
    perCategory: [...perCategory.entries()].map(([category, v]) => ({ category, ...v })),
    missed,
    needsSelfGrade,
  };
}

/** Records a short answer the student graded themselves on the results screen. */
export async function selfGrade(runId: string, item: Item, correct: boolean): Promise<void> {
  const run = await db.exams.get(runId);
  if (!run) return;
  const existing = run.answers[item.id];
  await db.exams.put({
    ...run,
    answers: {
      ...run.answers,
      [item.id]: { ...(existing ?? { itemId: item.id }), selfGraded: correct },
    },
  });
  await recordAnswer({ item, correct, mode: 'exam' });
}

export async function abandonExam(runId: string): Promise<void> {
  await db.exams.delete(runId);
}
